import React, { useState, useRef } from 'react';
import classNames from 'classnames';

import BaseInput, { BaseInputPropsWithoutChildren } from '../Input/BaseInput';
import OptionList, { OptionListOption } from '../OptionList/OptionList';

import isIE11 from '../../shared/utils/isIE11/isIE11';
import { CreateProps } from '../../types/utils/CreateProps';

import styles from './Dropdown.module.scss';

type OptionListOptionWithSecondaryLabel = OptionListOption & {
  secondaryLabel?: string;
};

type DropdownProps = CreateProps<
  {
    /** Label shown on the dropdown */
    label?: string;
    /** List of options */
    options: OptionListOption[];
    /** Selected option */
    selectedOption?: OptionListOptionWithSecondaryLabel;
    /** OnChange handler when user makes selection. Return value is the selected option as an object and native event */
    onChange: (
      event: React.SyntheticEvent<HTMLDivElement>,
      option: OptionListOption
    ) => void;
    /** Error text is shown if dropdown is not open and will disappear when user opens the dropdown  */
    errorText?: string;
    /** Additional description for input, if label is not enough  */
    helperText?: string;
    /** Placeholder is visible when the dropdown is empty */
    placeholder?: string;
    /** Sets the dropdown to disabled state. Dropdown is not clickable nor focusable */
    disabled?: boolean;
    /** Adds the required mark on the dropdown label */
    required?: boolean;
    /** Custom id */
    id?: string;
    /** Custom name */
    name?: string;
    /** Custom className */
    className?: string;
    /** Optional property to specify placeholder text displayed in the search input field */
    searchPlaceholder?: string;
  },
  BaseInputPropsWithoutChildren,
  | 'readonly'
  | 'iconName'
  | 'iconColor'
  | 'tabIndex'
  | 'onClick'
  | 'ref'
  | 'borderRadius'
  | 'onChange'
  | 'confirmed'
  | 'wrapperTabIndex'
  | 'value'
  | 'inputTabIndex'
>;

const Dropdown: React.FunctionComponent<DropdownProps> = ({
  options,
  onChange,
  selectedOption,
  errorText,
  disabled,
  className,
  searchPlaceholder,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [focusedOptionId, setFocusedOptionId] = useState<string>();

  const inputElement = useRef<HTMLInputElement>(null);
  const dropdownElement = useRef<HTMLDivElement>(null);

  const updateFocusedOptionId = () => {
    setFocusedOptionId(selectedOption?.value as string);
  };

  const focusOnFirstFocusableOption = () => {
    const firstFocusableOption = options.filter(
      (option) => !option.disabled
    )[0];

    setFocusedOptionId(firstFocusableOption.value);
  };

  const handleInputClick = () => {
    if (open) {
      setOpen(false);
    } else {
      if (!!selectedOption) {
        updateFocusedOptionId();
      }
      setOpen(true);
    }
  };

  const handleOptionListBlur = (
    event:
      | React.FocusEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>
  ) => {
    const isFocusEvent = (
      e: typeof event
    ): e is React.FocusEvent<HTMLDivElement> => 'relatedTarget' in e;

    /* This logic is related to the keyboard usability (closing on ESC) which is not supported in IE. That's why we leave IE out  */
    /* istanbul ignore else */
    if (!isIE11) {
      if (
        !isFocusEvent(event) ||
        !dropdownElement.current?.contains(event.relatedTarget as HTMLElement)
      ) {
        setOpen(false);
        inputElement.current?.focus();
      }
    }
  };

  const handleOnBlur = (
    event:
      | React.FocusEvent<HTMLDivElement>
      | React.KeyboardEvent<HTMLDivElement>
  ) => {
    const isFocusEvent = (
      e: typeof event
    ): e is React.FocusEvent<HTMLDivElement> => 'relatedTarget' in e;

    /* istanbul ignore else */
    if (!isIE11) {
      /* istanbul ignore else  */
      if (
        !isFocusEvent(event) ||
        (!dropdownElement.current?.contains(
          event.relatedTarget as HTMLElement
        ) &&
          open)
      ) {
        setOpen(false);
      }
    }

    /* istanbul ignore next  */
    if (isIE11 && typeof document !== 'undefined') {
      /* Prevent the onBlur in IE if user has clicked an option from the list or the scrollbar inside the optionlist */
      if (
        !!document.activeElement &&
        document.activeElement.className.indexOf('telia-react-optionlist') < 0
      ) {
        setOpen(false);
      }
    }
  };

  const handleOptionSelect = (
    event: React.SyntheticEvent<HTMLDivElement>,
    option: OptionListOption
  ) => {
    setOpen(false);
    inputElement.current?.focus();
    onChange(event, option);
  };

  const keyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleInputClick();
    }

    if (
      (event.key === 'ArrowDown' && !open) ||
      (event.key === 'ArrowUp' && !open)
    ) {
      event.preventDefault();
      if (!!selectedOption) {
        updateFocusedOptionId();
      } else {
        focusOnFirstFocusableOption();
      }
      setOpen(true);
    }
  };

  return (
    <div
      className={classNames([
        styles.dropdown,
        className,
        {
          [styles.dropdown_open]: open,
        },
      ])}
      ref={dropdownElement}
      onKeyDown={keyDown}
      onBlur={handleOnBlur}
    >
      <BaseInput
        {...props}
        disabled={disabled}
        wrapperTabIndex={-1}
        confirmed={false}
        iconName={open ? 'chevron-up' : 'chevron-down'}
        iconColor={open ? 'purple500' : 'black'}
        value={
          selectedOption
            ? selectedOption.secondaryLabel
              ? selectedOption.secondaryLabel
              : selectedOption.label
            : undefined
        }
        inputTabIndex={-1}
        readonly={true}
        onClick={disabled ? undefined : handleInputClick}
        ref={inputElement}
        errorText={errorText && errorText === ' ' ? ' ' : errorText}
        borderRadius={{ size: 'sm' }}
      />
      {!!open && (
        <OptionList
          options={options}
          onBlur={handleOptionListBlur}
          optionOnClick={handleOptionSelect}
          focusedOptionId={focusedOptionId}
          searchPlaceholder={searchPlaceholder}
        />
      )}
    </div>
  );
};

export default Dropdown;
